import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Globe, ChevronDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang, scrollTo } from "@/App";
import { LANGS, LANG_NAMES } from "@/i18n";

const LangPills = ({ dark }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid="language-switcher">
      <button
        data-testid="lang-menu-toggle"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 border px-2.5 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] transition-colors ${
          dark ? "border-paper/30 text-paper/80 hover:text-paper" : "border-line/70 text-ink2 hover:text-ink"
        }`}
      >
        <Globe size={12} />
        {lang}
        <ChevronDown size={11} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <button data-testid="lang-menu-overlay" aria-label="Close" onClick={() => setOpen(false)} className="fixed inset-0 z-[75] cursor-default" />
            <motion.div
              data-testid="lang-menu"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className={`absolute right-0 top-full mt-2 z-[80] w-60 max-h-80 overflow-y-auto border shadow-2xl ${
                dark ? "bg-inkdeep border-paper/20" : "bg-paper border-line"
              }`}
            >
              {LANGS.map((l) => (
                <button
                  key={l}
                  data-testid={`lang-switch-${l}`}
                  onClick={() => {
                    setLang(l);
                    setOpen(false);
                  }}
                  className={`w-full flex items-baseline justify-between gap-3 px-4 py-2.5 text-left transition-colors ${
                    lang === l
                      ? dark ? "text-gold bg-paper/5" : "text-terra bg-parchment/70"
                      : dark ? "text-paper/70 hover:text-paper hover:bg-paper/5" : "text-ink2 hover:text-ink hover:bg-parchment/70"
                  }`}
                >
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.15em]">{l}</span>
                  <span className="font-display text-sm">{LANG_NAMES[l]}</span>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "biographie", label: t.nav.bio },
    { id: "activites", label: t.nav.activities },
    { id: "livre", label: t.nav.book },
    { id: "sculptures", label: t.nav.sculptures },
    { id: "publications", label: t.nav.publications },
    { id: "inventions", label: t.nav.inventor },
    { id: "fetes", label: t.nav.fetes },
    { id: "manifeste", label: t.nav.manifesto },
  ];

  const go = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate("/");
      setTimeout(() => scrollTo(id), 400);
    } else {
      scrollTo(id);
    }
  };

  const light = !scrolled && onHome;

  return (
    <>
      <motion.header
        data-testid="main-header-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-[70] transition-all duration-500 ${
          scrolled || !onHome ? "bg-paper/85 backdrop-blur-md border-b border-line/70" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between gap-6">
          <button
            data-testid="header-logo"
            onClick={() => go("top")}
            className={`font-display text-lg md:text-xl tracking-tight leading-none transition-colors ${light ? "text-paper [text-shadow:_0_1px_12px_rgba(23,19,16,0.75)]" : "text-ink"}`}
          >
            Gilles <span className={`italic ${light ? "text-terraBright" : "text-terra"}`}>de Buretel</span> de Chassey
          </button>

          <nav className="hidden xl:flex items-center gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`link-underline font-mono2 text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  light ? "text-paper/80 hover:text-paper" : "text-ink2 hover:text-terra"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-4">
            <LangPills dark={light} />
            <button
              data-testid="header-cta-contact"
              onClick={() => go("contact")}
              className="group inline-flex items-center gap-2 bg-terra text-paper hover:bg-terraBright transition-colors px-5 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em]"
            >
              {t.nav.cta}
              <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className={`xl:hidden p-2 ${light ? "text-paper" : "text-ink"}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[65] bg-inkdeep/97 backdrop-blur-sm flex flex-col justify-center px-8"
          >
            <div className="space-y-2">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.07 * i, duration: 0.5 }}
                  onClick={() => go(l.id)}
                  className="block font-display text-3xl md:text-4xl text-paper hover:text-terra transition-colors text-left"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-nav-contact"
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                onClick={() => go("contact")}
                className="block font-display text-3xl md:text-4xl italic text-terra text-left"
              >
                {t.nav.contact}
              </motion.button>
            </div>
            <div className="mt-12">
              <LangPills dark />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
